/**
 * MIPS Overview (P5) — replaces the placeholder dashboard per the locked
 * design: multi-practice clients land on a practice PORTFOLIO that doubles as
 * the selector; a selected practice (or single-practice clients directly) get
 * the substantial practice overview. Selection uses the SAME mechanism as the
 * top-bar selector (AuthContext setters + session keys + DB persistence).
 * (docs/reference/app-plus/IMPLEMENTATION_PLAN.md, P5)
 */
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Building } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import userStorage from '@/lib/userStorage';
import { apiClient } from '@/lib/api-client';
import { NoPracticeState } from '@/components/shared/NoPracticeState';
import { PracticePortfolio } from './overview/PracticePortfolio';
import { PracticeOverview } from './overview/PracticeOverview';

interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias?: string;
}

interface Client {
  id: number;
  client_alias: string;
  account_id?: string;
  practices: Practice[];
}

export default function Dashboard() {
  const {
    clients,
    loading: authLoading,
    selectedPractice,
    setSelectedPractice,
    setSelectedClient,
  } = useAuth();
  const [showPortfolio, setShowPortfolio] = useState(false);

  // Resolve the session client SYNCHRONOUSLY (no effect/state hop): the old
  // navigate('/') redirect on a missing selection caused a flash-bounce to
  // Home. Same rule as APP Plus's NoAcoState — show a clean empty state.
  const client = useMemo<Client | null>(() => {
    if (authLoading || clients.length === 0) return null;
    const clientIdFromStorage = userStorage.getSessionItem('selectedClientId');
    if (!clientIdFromStorage) return null;
    return clients.find(c => c.id.toString() === clientIdFromStorage) ?? null;
  }, [clients, authLoading]);

  // Same selection mechanism as TopPagePracticeSelector (BINDING behavior #2).
  const handleSelectPractice = (practice: Practice) => {
    if (!client) return;
    setSelectedClient(client);
    setSelectedPractice(practice);
    userStorage.setSessionItem('selectedClientId', client.id.toString());
    userStorage.setSessionItem('selectedPracticeId', practice.id.toString());
    userStorage.setSessionItem('currentPractice', practice.id.toString());
    setShowPortfolio(false);
    // Persist selection to the database (same as the legacy dashboard did).
    apiClient.post('/api/user/selections', { clientId: client.id, practiceId: practice.id })
      .catch((error: unknown) => console.error('Failed to save practice selection:', error));
  };

  if (authLoading) {
    return (
      <div>
        <Helmet><title>Loading Dashboard…</title></Helmet>
      </div>
    );
  }
  if (!client) {
    return (
      <div>
        <Helmet><title>Overview – MIPS</title></Helmet>
        <NoPracticeState />
      </div>
    );
  }

  const multiPractice = client.practices.length > 1;
  // Portfolio shows when: multi-practice AND (nothing selected OR user asked for it)
  const activePractice = selectedPractice && client.practices.find(p => p.id === selectedPractice.id)
    ? (selectedPractice as Practice)
    : null;
  const showingPortfolio = multiPractice && (showPortfolio || !activePractice);

  return (
    <div>
      <Helmet>
        <title>
          {showingPortfolio
            ? `Practice Portfolio – ${client.client_alias}`
            : `Overview – ${activePractice?.practice_alias || activePractice?.tin_name || client.client_alias}`}
        </title>
        <link rel="canonical" href="/dashboard" />
      </Helmet>

      {showingPortfolio ? (
        <PracticePortfolio
          practices={client.practices}
          clientAlias={client.client_alias}
          onSelect={handleSelectPractice}
        />
      ) : activePractice ? (
        <>
          {multiPractice && (
            <div className="mb-4">
              <Button variant="ghost" size="sm" onClick={() => setShowPortfolio(true)}>
                <Building className="mr-2 h-4 w-4" />
                All practices ({client.practices.length})
              </Button>
            </div>
          )}
          <PracticeOverview practice={activePractice} />
        </>
      ) : (
        // Single-practice client with nothing selected yet: auto-select it.
        <AutoSelectSingle client={client} onSelect={handleSelectPractice} />
      )}
    </div>
  );
}

function AutoSelectSingle({ client, onSelect }: { client: Client; onSelect: (p: Practice) => void }) {
  useEffect(() => {
    if (client.practices.length >= 1) onSelect(client.practices[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);
  return <div className="flex h-64 items-center justify-center text-muted-foreground">Opening practice…</div>;
}
